import React, { useState, useEffect, useContext, useRef } from "react";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Row,
  Col,
  Upload,
  UploadFile,
  Modal,
} from "antd";
import {
  deleteTaskPhoto,
  getallBoard,
  getallDeveloperForTask,
  getallReporterLoginUser,
  getProjectName,
} from "../../utils/API";
import dayjs from "dayjs";
import { addTask, getTaskByID, updateTask } from "../../utils/API";
import { useNavigate, useParams } from "react-router-dom";
import { MdDeleteOutline } from "react-icons/md";
import { RcFile, UploadChangeParam } from "antd/es/upload";
import { BsUpload } from "react-icons/bs";
import { uniqueId } from "lodash";
import { AuthContext } from "../../Auth/AuthContext";

// TipTap imports
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";

// Custom image extension with size control
const CustomImage = Image.extend({
  addAttributes() {
    return {
      width: {
        default: "150px", // Default smaller width
      },
      height: {
        default: "auto",
      },
      // Include any existing attributes from the base Image extension if needed
      // You can manually copy known attributes here if required
      src: {},
      alt: {},
      title: {},
    };
  },
});

const { Option } = Select;

interface EditTaskModalProps {
  visible: boolean;
  taskId?: string | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditTaskModal: React.FC<EditTaskModalProps> = ({
  visible,
  taskId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [developers, setDevelopers] = useState<
    { _id: string; username: string }[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const IMAGEURL = process.env.REACT_APP_IMAGE_URL + "/task/";
  const [reporters, setReporters] = useState<
    { _id: string; reporterName: string }[]
  >([]);
  const [task, setTask] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [isStatusDisabled, setIsStatusDisabled] = useState(true);
  const [boards, setBoards] = useState<{ _id: string; name: string }[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const { authData } = useContext(AuthContext);
  const [descValue, setDescValue] = useState("");
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [ProjectName, setProjectName] = useState<any>([]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedImageDeleteIcon, setSelectedImageDeleteIcon] = useState<{
    visible: boolean;
    top: number;
    left: number;
    pos: number | null;
  }>({
    visible: false,
    top: 0,
    left: 0,
    pos: null,
  });

  const isAdmin = authData.roleId?.roleId === 1;

  useEffect(() => {
    if (!visible) {
      setSelectedImageDeleteIcon({
        visible: false,
        top: 0,
        left: 0,
        pos: null,
      });
    }
  }, [visible]);

  const normalizeTaskPhotos = (photo: any): string[] => {
    if (Array.isArray(photo)) {
      return photo.filter(Boolean);
    }

    return photo ? [photo] : [];
  };

  const getTaskPhotoNameFromSrc = (src: string) => {
    if (!src || src.startsWith("data:")) {
      return "";
    }

    const cleanSrc = src.split("?")[0].split("#")[0];
    const imageBase = IMAGEURL.replace(/\/$/, "");
    const normalizedSrc = cleanSrc.replace(/\\/g, "/");
    let photoName = "";

    if (normalizedSrc.startsWith(imageBase)) {
      photoName = normalizedSrc.slice(imageBase.length).replace(/^\/+/, "");
    } else {
      const taskPathIndex = normalizedSrc.lastIndexOf("/task/");
      photoName =
        taskPathIndex >= 0 ? normalizedSrc.slice(taskPathIndex + 6) : "";
    }

    try {
      return decodeURIComponent(photoName);
    } catch {
      return photoName;
    }
  };

  const getTaskPhotoUrl = (photoName: string) =>
    `${IMAGEURL}${encodeURIComponent(photoName)}`;

  const getTaskPhotoNamesFromHtml = (html: string) => {
    const container = document.createElement("div");
    container.innerHTML = html;

    return Array.from(container.querySelectorAll("img"))
      .map((img) => getTaskPhotoNameFromSrc(img.getAttribute("src") || ""))
      .filter(Boolean);
  };

  const removeTemporaryImagesFromHtml = (html: string) => {
    const container = document.createElement("div");
    container.innerHTML = html;

    container.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src") || "";

      if (src.startsWith("data:") || src.startsWith("blob:")) {
        img.remove();
      }
    });

    return container.innerHTML;
  };

  const showImageDeleteIcon = (imageNode: HTMLElement, pos: number | null) => {
    const container = editorContainerRef.current;

    if (!container || !(imageNode instanceof HTMLElement)) {
      setSelectedImageDeleteIcon({ visible: true, top: 8, left: 8, pos });
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const imageRect = imageNode.getBoundingClientRect();
    const left = Math.min(
      Math.max(imageRect.right - containerRect.left - 32, 8),
      Math.max(container.clientWidth - 36, 8),
    );
    const top = Math.max(imageRect.top - containerRect.top + 6, 8);

    setSelectedImageDeleteIcon({ visible: true, top, left, pos });
  };

  const updateSelectedImageDeleteIcon = (editorInstance: any) => {
    const selectedNode = editorInstance?.state?.selection?.node;
    if (selectedNode?.type?.name !== "image") {
      setSelectedImageDeleteIcon((prev) =>
        prev.visible ? { ...prev, visible: false, pos: null } : prev,
      );
      return;
    }

    const imageNode = editorInstance.view.nodeDOM(
      editorInstance.state.selection.from,
    ) as HTMLElement | null;

    if (imageNode instanceof HTMLElement) {
      showImageDeleteIcon(imageNode, editorInstance.state.selection.from);
    }
  };

  const handleEditorImageHover = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!editor) return;

    const target = event.target as HTMLElement;
    if (target.closest(".task-editor-image-delete-icon")) return;

    const imageNode = target.closest(".ProseMirror img") as HTMLElement | null;
    if (imageNode) {
      const pos = editor.view.posAtDOM(imageNode, 0);
      showImageDeleteIcon(imageNode, pos);
      return;
    }

    const selectedNode = (editor.state.selection as any).node;
    if (selectedNode?.type?.name !== "image") {
      setSelectedImageDeleteIcon((prev) =>
        prev.visible ? { ...prev, visible: false, pos: null } : prev,
      );
    }
  };

  const handleEditorImageLeave = () => {
    if (!editor) return;

    const selectedNode = (editor.state.selection as any).node;
    if (selectedNode?.type?.name !== "image") {
      setSelectedImageDeleteIcon((prev) =>
        prev.visible ? { ...prev, visible: false, pos: null } : prev,
      );
    }
  };

  // TipTap editor instance with custom image
  const editor = useEditor({
    extensions: [
      StarterKit,
      CustomImage,
      Placeholder.configure({
        placeholder: "Enter task description or upload image",
        showOnlyWhenEditable: true,
      }),
    ],
    content: descValue || "",
    editable: true,
    onTransaction({ editor }) {
      // optional: you can capture changes here
    },
    onSelectionUpdate({ editor }) {
      updateSelectedImageDeleteIcon(editor);
    },
  });

  const addImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;

      setUploadedImages((prev) => [...prev, file]);

      const reader = new FileReader();
      reader.onload = () => {
        //@ts-ignore
        editor
          ?.chain()
          .focus()
          .setImage({ src: reader.result as string, width: 200 })
          .run();
      };
      reader.readAsDataURL(file);
    };
  };

  const deleteSelectedImage = () => {
    if (!editor) return;

    const selectedNode = (editor.state.selection as any).node;
    if (selectedNode?.type?.name === "image") {
      editor.chain().focus().deleteSelection().run();
      setSelectedImageDeleteIcon({
        visible: false,
        top: 0,
        left: 0,
        pos: null,
      });
      return;
    }

    const imagePos = selectedImageDeleteIcon.pos;
    const imageRange =
      imagePos !== null
        ? [imagePos, imagePos - 1]
            .filter((pos) => pos >= 0)
            .map((pos) => ({ pos, node: editor.state.doc.nodeAt(pos) }))
            .find(({ node }) => node?.type.name === "image")
        : null;

    if (!imageRange?.node) {
      message.info("Click an image first, then delete it.");
      return;
    }

    editor
      .chain()
      .focus()
      .deleteRange({
        from: imageRange.pos,
        to: imageRange.pos + imageRange.node.nodeSize,
      })
      .run();
    setSelectedImageDeleteIcon({ visible: false, top: 0, left: 0, pos: null });
  };

  const handleDeadlineDateChange = (date: dayjs.Dayjs | null) => {
    try {
      // Update form value immediately
      form.setFieldValue("deadlineDate", date);

      // Optional: Show feedback to user
      // if (date) {
      //     message.success(`Deadline set to: ${date.format('DD-MM-YYYY HH:mm')}`, 2);
      // }

      // Auto-close the picker after a short delay
      setTimeout(() => {
        setDatePickerOpen(false);
      }, 300);
    } catch (error) {
      console.error("Error setting deadline date:", error);
    }
  };
  // Fetch all necessary data on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch boards
        const boardsResponse = await getallBoard("", 1);
        setBoards(boardsResponse.data);

        // Fetch developers
        const devResponse = await getallDeveloperForTask("", "");
        setDevelopers(devResponse?.data || []);

        // Fetch reporters
        const repResponse = await getallReporterLoginUser("");
        setReporters(repResponse?.data || []);
      } catch (error) {
        message.error("Failed to load initial data");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch task data when modal becomes visible
  const fetchTask = async () => {
    if (!taskId) return;

    setLoading(true);
    try {
      const data = await getTaskByID(taskId);
      setTask(data);

      if (data) {
        form.setFieldsValue({
          userId: data.userId?._id,
          boardId: data.boardId,
          taskName: data.taskName,
          startDate: data.startDate ? dayjs(data.startDate) : null,
          deadlineDate: data.deadlineDate ? dayjs(data.deadlineDate) : null,
          status: data.status,
          priority: data.priority,
          assignees: data.assignees?.map((assignee: any) => assignee._id),
          reporterId: data.reporter?._id,
          isActive: true,
          isDeleted: false,
          subTasks:
            data.subTasks?.map((subtask: any) => ({
              subTaskName: subtask.subTaskName,
              subTaskDesc: subtask.subTaskDesc,
              status: subtask.status,
              assignees: subtask.assignees?.map((a: any) => a._id) || [], // ✅ only IDs
              isActive: true,
            })) || [],
        });

        let editorContent = data.taskDesc || "";
        const photos = normalizeTaskPhotos(data.photo);

        if (photos.length > 0) {
          editorContent = removeTemporaryImagesFromHtml(editorContent);
          const existingPhotoNames = getTaskPhotoNamesFromHtml(editorContent);
          const missingPhotos = photos.filter(
            (img) => !existingPhotoNames.includes(img),
          );
          const imageHTML = missingPhotos
            .map(
              (img: string) =>
                `<p><img src="${getTaskPhotoUrl(img)}" width="300" /></p>`,
            )
            .join("");
          editorContent = `${imageHTML}${editorContent}`;
        }

        if (editor) {
          editor.commands.setContent(editorContent);
        }

        if (photos.length > 0) {
          setFileList(
            photos.map((img: string, index: number) => ({
              uid: `existing-${index}`,
              name: img.split("/").pop() || `photo-${index}`,
              status: "done",
              url: getTaskPhotoUrl(img),
            })),
          );
        } else {
          setFileList([]);
        }
      }
    } catch (error) {
      message.error("Failed to fetch task data.");
    } finally {
      setLoading(false);
    }
  };
  const fetchProjectNames = async () => {
    try {
      const response: any = await getProjectName();
      setProjectName(response.data);
    } catch {
      message.error("Failed to load reporters");
      ProjectName([]);
    }
  };

  useEffect(() => {
    fetchProjectNames();
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const formData = new FormData();

      // Append simple fields
      const simpleFields = [
        "userId",
        "boardId",
        "taskName",
        "status",
        "priority",
        "reporterId",
      ];

      simpleFields.forEach((field) => {
        if (values[field] !== undefined && values[field] !== null) {
          formData.append(field, values[field]);
        }
      });

      const htmlContent = editor?.getHTML() || "";
      const hasText = editor?.getText().trim().length || 0;
      const hasImages = /<img\b/i.test(htmlContent);

      if (!hasText && !hasImages) {
        message.error("Please enter a task description or upload an image");
        setIsLoading(false);
        return;
      }

      formData.append("taskDesc", htmlContent);
      const remainingPhotos = getTaskPhotoNamesFromHtml(htmlContent);
      formData.append("remainingPhotos", JSON.stringify(remainingPhotos));

      uploadedImages.forEach((file: File) => {
        formData.append("photo", file);
      });

      if (remainingPhotos.length === 0 && uploadedImages.length === 0) {
        formData.append("removePhoto", "true");
      }

      // Dates
      if (values.startDate) {
        formData.append("startDate", values.startDate.format());
      }
      if (values.deadlineDate) {
        formData.append("deadlineDate", values.deadlineDate.format());
      }

      // Assignees
      (values.assignees || []).forEach((assigneeId: string) => {
        formData.append("assignees[]", assigneeId);
      });

      // SubTasks
      (values.subTasks || []).forEach((subtask: any, index: number) => {
        formData.append(`subTasks[${index}][subTaskName]`, subtask.subTaskName);
        formData.append(`subTasks[${index}][subTaskDesc]`, subtask.subTaskDesc);
        formData.append(`subTasks[${index}][status]`, subtask.status);
        (subtask.assignees || []).forEach((a: string, i: number) => {
          formData.append(`subTasks[${index}][assignees][${i}]`, a);
        });
        formData.append(`subTasks[${index}][isActive]`, "true");
      });

      const response = await updateTask(taskId as string, formData);

      if (response && (response.status === 200 || response.status === 201)) {
        message.success("Task updated successfully!");

        const updatedTask = response.data?.data || response.data;

        if (onSuccess) onSuccess();
      } else {
        message.error(response?.data?.message || "Failed to update task");
      }
    } catch (error: any) {
      message.error(
        "Task update failed: " +
          (error.response?.data?.message || "Server error"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editor) return;

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();

      const files = Array.from(event.dataTransfer?.files || []);
      files.forEach((file) => {
        if (!file.type.startsWith("image/")) return;

        const reader = new FileReader();
        reader.onload = () => {
          //@ts-ignore
          editor
            .chain()
            .focus()
            .setImage({ src: reader.result as string, width: 200 })
            .run();
          setUploadedImages((prev) => [...prev, file]);
        };
        reader.readAsDataURL(file);
      });
    };

    const handlePaste = (event: ClipboardEvent) => {
      const items = Array.from(event.clipboardData?.items || []);
      items.forEach((item) => {
        if (item.type.indexOf("image") === -1) return;
        const file = item.getAsFile();
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
          //@ts-ignore
          editor
            .chain()
            .focus()
            .setImage({ src: reader.result as string, width: 200 })
            .run();
          setUploadedImages((prev) => [...prev, file]);
        };
        reader.readAsDataURL(file);
      });
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener("drop", handleDrop);
    editorElement.addEventListener("paste", handlePaste);

    return () => {
      editorElement.removeEventListener("drop", handleDrop);
      editorElement.removeEventListener("paste", handlePaste);
    };
  }, [editor]);

  const handleImageChange = ({ fileList }: UploadChangeParam<UploadFile>) => {
    setFileList(fileList);
  };

  const addSubtask = () => {
    form.setFieldsValue({
      subTasks: [
        ...(form.getFieldValue("subTasks") || []),
        {
          subTaskName: "",
          subTaskDesc: "",
          status: "Pending",
          assignees: [],
        },
      ],
    });
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setDescValue("");
      setUploadedImages([]);
      editor?.commands.setContent("");
      setFileList([]);
    } else if (taskId && editor) {
      fetchTask();
    }
  }, [visible, taskId, editor, form]);

  function add(): void {
    form.setFieldsValue({
      subTasks: [
        ...(form.getFieldValue("subTasks") || []),
        {
          subTaskName: "",
          subTaskDesc: "",
          status: "Pending",
          assignees: [],
        },
      ],
    });
  }

  const handleDeletePhoto = async () => {
    try {
      setLoading(true);

      const response = await deleteTaskPhoto(taskId as string);

      if (response && (response.status === 200 || response.status === 201)) {
        message.success("Photo deleted successfully!");
        setFileList([]); // clear upload preview
        setTask((prev: any) => ({ ...prev, photo: null })); // clear task photo in state
      } else {
        message.error(response?.data?.message || "Failed to delete photo");
      }
    } catch (error: any) {
      message.error(
        "Photo deletion failed: " +
          (error.response?.data?.message || "Server error"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered
      className="custom-modal"
      bodyStyle={{
        overflowY: "auto",
        fontFamily: "inter, sans-serif",
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Edit Task
        </div>
      }
      // {/* <Form.Item>
      //           <div className="flex justify-center">
      //             <div className="relative w-20 h-20">
      //               <Upload.Dragger
      //                 name="avatar"
      //                 className="w-full h-full rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors bg-gray-50 overflow-hidden"
      //                 showUploadList={false}
      //                 listType="picture-card"
      //                 fileList={fileList}
      //                 beforeUpload={() => false}
      //                 onChange={handleImageChange}
      //                 accept=".png,.jpg,.jpeg"
      //               >
      //                 {!previewImage ? (
      //                   <div className="flex flex-col items-center p-1">
      //                     <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
      //                       <BsUpload className="text-gray-400 text-base" />
      //                     </div>
      //                     <span className="text-xs text-gray-400 mt-1">Profile</span>
      //                   </div>
      //                 ) : (
      //                   <img
      //                     src={previewImage}
      //                     alt="Uploaded"
      //                     className="w-full h-full object-cover"
      //                   />
      //                 )}
      //               </Upload.Dragger>

      //               {previewImage && (
      //                 <Button
      //                   type="text"
      //                   shape="circle"
      //                   size="small"
      //                   icon={<MdDeleteOutline size={16} />}
      //                   className="absolute top-0 right-0 bg-red-500 text-white hover:bg-red-600 shadow-sm"
      //                   onClick={(e) => {
      //                     e.stopPropagation();
      //                     { handleImageChange };
      //                     setFileList([]);
      //                   }}
      //                 />
      //               )}
      //             </div>
      //           </div>
      //         </Form.Item> */}
      // {/* borard */}
    >
      <Form
        onFinish={handleSubmit}
        form={form}
        layout="horizontal"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 19 }}
        colon={false}
      >
        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563", // Medium gray color similar to the image
                fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                fontSize: "14px", // Approximate font size based on the image
                paddingLeft: "15px",
                fontWeight: 500, // Medium weight to match "font-medium"
              }}
            >
              Board<span className="text-red-500">*</span>
            </span>
          }
          required={false}
          name="boardId"
          rules={[{ required: true, message: "Please select a board" }]}
        >
          <Select placeholder="Select Board" disabled={!isAdmin}>
            {boards.map((board: any) => (
              <Option key={board._id} value={board._id}>
                {board.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* <Form.Item
          label={<span style={{
            color: '#4b5563', // Medium gray color similar to the image
            fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
            fontSize: '14px', // Approximate font size based on the image
            fontWeight: 500, // Medium weight to match "font-medium"
          }}>Task Name<span className="text-red-500">*</span></span>}
          required={false}
          name="taskName"
          rules={[{ required: true, message: "Please enter task name" }]}
        >
          <Input placeholder="Enter task name" className="Allinput" />
        </Form.Item> */}

        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563",
                fontFamily: "inter, sans-serif",
                fontSize: "14px",
                fontWeight: 500,
                paddingLeft: "15px",
              }}
            >
              Task Name<span className="text-red-500">*</span>
            </span>
          }
          required={false}
          name="taskName"
          rules={[{ required: true, message: "Please enter task name" }]}
        >
          <Select placeholder="Select Project" showSearch>
            {ProjectName.map((p: any) => (
              <Option key={p._id} value={p.projectName}>
                {p.projectName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563", // Medium gray color similar to the image
                fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                fontSize: "14px", // Approximate font size based on the image
                fontWeight: 500,
                paddingLeft: "15px", // Medium weight to match "font-medium"
              }}
            >
              Description<span className="text-red-500">*</span>
            </span>
          }
        >
          <div className="border rounded bg-white p-2">
            <div className="flex gap-2">
              <div
                ref={editorContainerRef}
                className="p-3 min-h-[150px]"
                style={{ position: "relative" }}
                onMouseMove={handleEditorImageHover}
                onMouseLeave={handleEditorImageLeave}
              >
                {selectedImageDeleteIcon.visible && (
                  <button
                    type="button"
                    className="task-editor-image-delete-icon"
                    style={{
                      position: "absolute",
                      top: selectedImageDeleteIcon.top,
                      left: selectedImageDeleteIcon.left,
                      zIndex: 50,
                      background: "rgba(239, 68, 68, 0.9)",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "12px",
                      padding: 0,
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={deleteSelectedImage}
                    aria-label="Delete selected image"
                    title="Delete selected image"
                  >
                    <DeleteOutlined />
                  </button>
                )}
                <EditorContent
                  editor={editor}
                  className="min-h-[100px] outline-none p-2"
                />
              </div>
              <Button
                onClick={addImage}
                type="default"
                icon={<UploadOutlined />}
                style={{
                  border: "none",
                  boxShadow: "none",
                  alignSelf: "flex-start",
                }}
              />
            </div>
          </div>
        </Form.Item>

        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563", // Medium gray color similar to the image
                fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                fontSize: "14px", // Approximate font size based on the image
                fontWeight: 500, // Medium weight to match "font-medium"
                paddingLeft: "15px",
              }}
            >
              Deadline Date<span className="text-red-500">*</span>
            </span>
          }
          required={false}
          name="deadlineDate"
          rules={[{ required: true, message: "Please select deadline" }]}
        >
          <DatePicker
            showTime
            format="DD-MM-YYYY HH:mm"
            className="w-full"
            open={datePickerOpen}
            onOpenChange={(open) => {
              setDatePickerOpen(open);
              // When closing without selection, ensure we have the current value
              if (!open) {
                const currentValue = form.getFieldValue("deadlineDate");
                if (currentValue) {
                  handleDeadlineDateChange(currentValue);
                }
              }
            }}
            onChange={(date) => {
              // Auto-save when date changes (including time changes)
              handleDeadlineDateChange(date);
            }}
            //@ts-ignore
            onSelect={(date) => {
              // Immediate save when selecting from calendar
              handleDeadlineDateChange(date);
            }}
            onOk={() => {
              // Empty function - we don't need OK button
            }}
            // Remove the need for confirmation
            needConfirm={false}
            disabledDate={(current) => {
              return current && current < dayjs().startOf("day");
            }}
            showNow={true}
            // Optional: Auto-close when time is selected
            onPanelChange={(value, mode) => {
              if (mode === "time") {
                // When switching to time panel, you might want different behavior
              }
            }}
          />
        </Form.Item>

        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563", // Medium gray color similar to the image
                fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                fontSize: "14px", // Approximate font size based on the image
                fontWeight: 500, // Medium weight to match "font-medium"
                paddingLeft: "15px",
              }}
            >
              Priority<span className="text-red-500">*</span>
            </span>
          }
          required={false}
          name="priority"
          rules={[{ required: true, message: "Please select priority" }]}
        >
          <Select placeholder="Select Priority">
            <Option value="Low">Low</Option>
            <Option value="Medium">Medium</Option>
            <Option value="High">High</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563", // Medium gray color similar to the image
                fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                fontSize: "14px", // Approximate font size based on the image
                fontWeight: 500, // Medium weight to match "font-medium"
                paddingLeft: "15px",
              }}
            >
              Assignees<span className="text-red-500">*</span>
            </span>
          }
          required={false}
          name="assignees"
          rules={[
            { required: true, message: "Please select at least one assignee" },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="Select Assignees"
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {developers.map((dev) => (
              <Option key={dev._id} value={dev._id}>
                {dev.username}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563", // Medium gray color similar to the image
                fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                fontSize: "14px", // Approximate font size based on the image
                fontWeight: 500, // Medium weight to match "font-medium"
                paddingLeft: "15px",
              }}
            >
              Reporter<span className="text-red-500"></span>
            </span>
          }
          required={false}
          name="reporterId"
        >
          <Select placeholder="Select Reporter">
            {reporters.map((r) => (
              <Option key={r._id} value={r._id}>
                {r.reporterName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <span
              style={{
                color: "#4b5563", // Medium gray color similar to the image
                fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                fontSize: "14px", // Approximate font size based on the image
                fontWeight: 500, // Medium weight to match "font-medium"
                paddingLeft: "15px",
              }}
            >
              SubTasks<span className="text-red-500">*</span>
            </span>
          }
          required={false}
          wrapperCol={{ span: 18 }}
        >
          <Form.List name="subTasks">
            {(fields, { add, remove }) => (
              <div>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  className="mb-3"
                  onClick={() => add()}
                >
                  Add Subtask
                </Button>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    className="border rounded p-3 mb-3 bg-white shadow-sm"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <Form.Item {...restField} name={[name, "subTaskName"]}>
                        <Input
                          placeholder="Subtask name"
                          className="Allinput "
                        />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "subTaskDesc"]}>
                        <Input placeholder="Description" className="Allinput" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "status"]}>
                        <Select placeholder="Status">
                          <Option value="Pending">Pending</Option>
                          <Option value="In Progress">In Progress</Option>
                          <Option value="Completed">Completed</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item {...restField} name={[name, "assignees"]}>
                        <Select mode="multiple" placeholder="Assignees">
                          {developers.map((dev) => (
                            <Option key={dev._id} value={dev._id}>
                              {dev.username}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                    <Button danger type="text" onClick={() => remove(name)}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Form.List>
        </Form.Item>

        <div className="flex justify-end gap-3 pr-5">
          <button
            type="button"
            className="allcanclebutton"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button className="allsubmitebutton" type="submit">
            {isLoading ? "Updating..." : "Update"}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditTaskModal;
